import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { SkillState } from '@shared/index';
import { skillIdDepthSchema, skillIdsSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';

export type SkillTreeItem = {
  skillId: string;
  parent?: SkillTreeItem;
  ancestors: SkillTreeItem[];
  children: SkillTreeItem[];
  descendants: SkillTreeItem[];
  path: string[]
}

/**
 * Aktiviert, deaktiviert und verwaltet Skills zur Laufzeit (`RAGAI.SKILL`).
 * @remarks
 * Skills sind hierarchisch: Die Eltern-Kind-Beziehung wird rein aus den in der Skill-ID enthaltenen Zahlen
 * abgeleitet (siehe {@link getSkillPath}), nicht aus einer expliziten Baumstruktur in der Konfiguration. Über
 * `depth` in {@link enableSkillAndChildren}/{@link disableSkillAndChildren}/{@link getSkillIdChildren} lässt sich
 * die Rekursionstiefe begrenzen (Standard: unbegrenzt).
 * `enableSkills`/`disableSkills` ändern den dauerhaften Aktivierungsstatus, während {@link skillsForNextRun} Skills
 * nur für den unmittelbar nächsten KI-Aufruf erzwingt (unabhängig vom Aktivierungsstatus) und danach automatisch
 * über {@link unforceAllSkills} wieder aufgehoben wird.
 */
@Injectable({
  providedIn: 'root'
})
export class SkillService {
  public skillUserApi = "/api/toolbox/skill/user";

  constructor(
    private http: HttpClient,
    private botIdService: BotidService
  ) { }

  /**
   * Gibt den aktuellen Aktivierungsstatus aller Skills zurück.
   * @returns Liste aller Skills mit Status.
   */
  public async getSkills() {
    return await firstValueFrom(this.http.get<SkillState[]>(`${this.skillUserApi}/state/${this.botIdService.getBotId()}`));
  }

  /** Deaktiviert alle Skills. */
  public async disableAllSkills() {
    const skills = await this.getSkills();
    const skillIds = skills.map((s) => s.skillId);
    await this.disableSkills(skillIds);
  }

  /** Aktiviert alle Skills. */
  public async enableAllSkills() {
    const skills = await this.getSkills();
    const skillIds = skills.map((s) => s.skillId);
    await this.enableSkills(skillIds);
  }

  /**
   * Aktiviert die angegebenen Skills.
   * @param skillIds IDs der zu aktivierenden Skills, oder ein Objekt gemäß `skillIdsSchema`. `["___ALL___"]` aktiviert alle.
   * @returns Der aktualisierte Skill-Status.
   */
  public async enableSkills(skillIds: string[] | z.input<typeof skillIdsSchema>): Promise<{ skills: SkillState[] }> {
    const parsed = skillIdsSchema.safeParse(skillIds);
    if (parsed.success && !Array.isArray(skillIds)) {
      skillIds = parsed.data.skillIds;
    }
    // @ts-expect-error
    if (skillIds.length === 1 && skillIds[0] === "___ALL___") {
      // @ts-expect-error
      return await this.enableAllSkills();
    }

    skillIds = (skillIds as string[]).filter((skillId) => skillId !== '');
    return await firstValueFrom(this.http.post<{ skills: SkillState[] }>(`${this.skillUserApi}/enable/${this.botIdService.getBotId()}`, { skillIds }));
  }

  /**
   * Deaktiviert die angegebenen Skills.
   * @param skillIds IDs der zu deaktivierenden Skills, oder ein Objekt gemäß `skillIdsSchema`. `["___ALL___"]` deaktiviert alle.
   * @returns Der aktualisierte Skill-Status.
   */
  public async disableSkills(skillIds: string[] | z.input<typeof skillIdsSchema>): Promise<{ skills: SkillState[] }> {
    const parsed = skillIdsSchema.safeParse(skillIds);
    if (parsed.success && !Array.isArray(skillIds)) {
      skillIds = parsed.data.skillIds;
    }

    // @ts-expect-error
    if (skillIds.length === 1 && skillIds[0] === "___ALL___") {
      // @ts-expect-error
      return await this.disableAllSkills();
    }

    skillIds = (skillIds as string[]).filter((skillId) => skillId !== '');
    return await firstValueFrom(this.http.post<{ skills: SkillState[] }>(`${this.skillUserApi}/disable/${this.botIdService.getBotId()}`, { skillIds }));
  }

  /**
   * Aktiviert einen Skill sowie dessen Kinder-Skills bis zu einer bestimmten Tiefe.
   * @param skillId ID des Skills, oder ein Objekt gemäß `skillIdDepthSchema`.
   * @param depth Maximale Tiefe der Kinder-Skills. Standard: unbegrenzt.
   * @returns Der aktualisierte Skill-Status.
   */
  public async enableSkillAndChildren(skillId: string | z.input<typeof skillIdDepthSchema>, depth = Number.MAX_SAFE_INTEGER): Promise<{ skills: SkillState[] }> {
    const parsed = skillIdDepthSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      depth = parsed.data.depth ?? depth;
      skillId = parsed.data.skillId;
    }
    return await this.enableSkills(await this.getSkillIdChildren(skillId as string, depth));
  }

  /**
   * Deaktiviert einen Skill sowie dessen Kinder-Skills bis zu einer bestimmten Tiefe.
   * @param skillId ID des Skills, oder ein Objekt gemäß `skillIdDepthSchema`.
   * @param depth Maximale Tiefe der Kinder-Skills. Standard: unbegrenzt.
   * @returns Der aktualisierte Skill-Status.
   */
  public async disableSkillAndChildren(skillId: string | z.input<typeof skillIdDepthSchema>, depth = Number.MAX_SAFE_INTEGER): Promise<{ skills: SkillState[] }> {
    const parsed = skillIdDepthSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      depth = parsed.data.depth ?? depth;
      skillId = parsed.data.skillId;
    }
    return await this.disableSkills(await this.getSkillIdChildren(skillId as string, depth));
  }

  /**
   * Legt fest, welche Skills beim nächsten Bot-Durchlauf erzwungen aktiv sind.
   * @param skillIds IDs der zu erzwingenden Skills, oder ein Objekt gemäß `skillIdsSchema`.
   * @returns Der aktualisierte Skill-Status.
   */
  public async skillsForNextRun(skillIds: string[] | z.input<typeof skillIdsSchema>): Promise<{ skills: SkillState[] }> {
    const parsed = skillIdsSchema.safeParse(skillIds);
    if (parsed.success && !Array.isArray(skillIds)) {
      skillIds = parsed.data.skillIds;
    }
    return await firstValueFrom(this.http.post<{ skills: SkillState[] }>(`${this.skillUserApi}/force/${this.botIdService.getBotId()}`, { skillIds: skillIds as string[] }));
  }
  /**
   * Hebt die Erzwingung für alle Skills wieder auf.
   * @returns Der aktualisierte Skill-Status.
   */
  public async unforceAllSkills() {
    return await firstValueFrom(this.http.post<{ skills: SkillState[] }>(`${this.skillUserApi}/unforce/${this.botIdService.getBotId()}`, {}));
  }

  /**
   * Gibt die IDs eines Skills und all seiner Kinder-Skills bis zu einer bestimmten Tiefe zurück.
   * @param skillId ID des Wurzel-Skills.
   * @param depth Maximale Tiefe. Standard: unbegrenzt.
   * @returns Liste der Skill-IDs.
   */
  public async getSkillIdChildren(skillId: string, depth: number = Number.MAX_SAFE_INTEGER): Promise<string[]> {
    const skills = await this.getSkills();
    const skillTree = this.skillIdsToTreeItems(skills.map((skill) => skill.skillId));;
    const skillInTree = skillTree.find(ti => ti.skillId === skillId);
    if (!skillInTree) {
      return [];
    }
    const result = this.getChildSkillsRecursive(skillInTree, depth);
    return result;
  }

  /**
   * Gibt die Verschachtelungstiefe eines Skills in der Skill-Hierarchie zurück.
   * @param skillId ID des Skills.
   * @returns Die Tiefe (0 = Wurzelebene).
   */
  public getSkillDepth(skillId: string) {
    return this.getSkillPath(skillId).length - 1;
  }

  /**
   * Zerlegt eine Skill-ID in ihren hierarchischen Pfad (basierend auf enthaltenen Zahlen).
   * @param skillId ID des Skills.
   * @returns Der Pfad als Array von Segmenten.
   */
  public getSkillPath(skillId: string): string[] {
    const path: string[] = [];
    let lastWasNum = false;
    let lastChars = "";
    for (const c of skillId) {
      if (!this.isNum(c)) {
        lastWasNum = false;
        lastChars += c;
      } else {
        if (lastWasNum) {
          const lastValue = path.at(path.length - 1);
          if (lastValue !== undefined) {
            path[path.length - 1] = lastChars + lastValue + c;
            lastChars = "";
          }
        } else {
          path.push(lastChars + c);
          lastChars = "";
        }
        lastWasNum = true;
      }
    }
    if (path.length === 0) {
      path.push(skillId);
    }
    return path;
  }

  /**
   * Baut aus einer flachen Liste von Skill-IDs die Wurzel-Elemente des Skill-Baums.
   * @param skillIds Alle Skill-IDs.
   * @returns Die Wurzel-Elemente des Baums.
   */
  public getTreeFromSkillIds(skillIds: string[]) {
    return this.skillIdsToTreeItems(skillIds).filter((skill) => skill.ancestors.length === 0);
  }

  /**
   * Baut aus einer flachen Liste von Skill-IDs den vollständigen Skill-Baum (Eltern/Kinder/Vorfahren/Nachfahren).
   * @param skillIds Alle Skill-IDs.
   * @returns Alle Baum-Elemente.
   */
  public skillIdsToTreeItems(skillIds: string[]) {
    const treeItems: SkillTreeItem[] = skillIds.map((skillId) => {
      return {
        path: this.getSkillPath(skillId),
        children: [],
        descendants: [],
        ancestors: [],
        skillId: skillId
      };
    });
    for (const item of treeItems) {
      const parent = treeItems.find((parentItem) => {
        return this.isDirectParent(parentItem, item);
      });
      if (parent) {
        item.parent = parent;
        parent.children.push(item);
      }
      const ancestors = treeItems.filter((parentItem) => {
        return this.isDirectOrIndirectParent(parentItem, item);
      });
      for (const ancestor of ancestors) {
        item.ancestors.push(ancestor);
        ancestor.descendants.push(item);
      }
    }
    return treeItems;
  }

  private getChildSkillsRecursive(skillTree: SkillTreeItem, targetDepths: number, depth: number = 0): string[] {
    const result = [skillTree.skillId];
    if (targetDepths == depth) {
      return result;
    }
    result.push(...skillTree.children.map((child) => child.skillId),
      ...skillTree.children.map((child) => this.getChildSkillsRecursive(child, targetDepths, depth + 1)).flat()
    );
    return result;
  }

  private isDirectOrIndirectParent(parent: SkillTreeItem, child: SkillTreeItem) {
    if (parent.skillId === child.skillId || parent.path.length >= child.path.length) {
      return false;
    }
    for (let i = 0; i < parent.path.length; i++) {
      if (parent.path[i] !== child.path[i]) {
        return false;
      }
    }
    return true;
  }

  private isDirectParent(parent: SkillTreeItem, child: SkillTreeItem) {
    if (parent.skillId === child.skillId || parent.path.length >= child.path.length) {
      return false;
    }
    for (let i = 0; i < child.path.length - 1; i++) {
      if (parent.path[i] !== child.path[i]) {
        return false;
      }
    }
    return true;
  }

  private isNum(char: string) {
    return char >= '0' && char <= '9';
  }
}
